import { useState } from 'react';
import { useServiceCharges } from './InvoiceDetailsPopup.hooks';
import {
    transformAPIDataToServiceCharges,
    calculateInvoiceSummary,
    parseCompanyAndSeller,
    getEarliestCreatedDate,
    calculatePeriodToDate,
    getWarehouseNames,
    isInvoicePaid,
    getCategoryTotals,
} from '../utils/InvoiceDetailsPopup.utils';
import { generateInvoicePDFDirect } from '../../../../utils/pdfGenerator';

/**
 * usePdfDownload - Hook to manage PDF download workflow
 * Orchestrates: fetch service charges → transform → calculate → generate PDF
 * @returns {Object} { isLoading: boolean, error: string|null, downloadInvoicePDF: function }
 */
export const usePdfDownload = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    /**
     * Download invoice as PDF
     * @param {Object} invoice - Invoice record with id, invoiceNumber, etc.
     */
    const downloadInvoicePDF = async invoice => {
        try {
            // Reset state
            setIsLoading(true);
            setError(null);

            if (!invoice?.id) {
                throw new Error('Invalid invoice: missing invoice ID');
            }

            console.log('Starting PDF download for invoice:', invoice.invoiceNumber);

            // Step 1: Fetch service charges from API
            // This mimics the useServiceCharges hook but executes immediately
            const { apiData: serviceChargesApiData } = await fetchServiceChargesData(invoice);

            if (!serviceChargesApiData) {
                throw new Error('Failed to fetch invoice service charges');
            }

            console.log('✓ Service charges fetched');

            // Step 2: Parse company and seller
            const { company: displayCompany, seller: displaySeller } = parseCompanyAndSeller(
                invoice.companyName
            );

            // Step 3: Extract hasRentItems for cycle determination
            const hasRentItems = invoice.hasRentItems || false;

            // Step 4: Transform API data
            const serviceCharges = transformAPIDataToServiceCharges(serviceChargesApiData);
            console.log('✓ Service charges transformed');

            // Step 5: Extract period dates (stored invoice period, else earliest charge date)
            const periodFromDate = invoice.fromDate || getEarliestCreatedDate(serviceChargesApiData);
            const periodToDate = invoice.toDate || calculatePeriodToDate(periodFromDate, hasRentItems);
            const isPaid = isInvoicePaid(invoice.status);

            // Step 6: Calculate summary
            const taxRate = 0;
            const { subtotal, totalAdditionalCharges, tax, totalDue } = calculateInvoiceSummary(
                serviceCharges,
                taxRate
            );
            console.log('✓ Summary calculated');

            // Step 7: Generate PDF
            await generateInvoicePDFDirect(
                { ...invoice, warehouseName: getWarehouseNames(serviceCharges) },
                serviceCharges,
                {
                    subtotal,
                    totalAdditionalCharges,
                    tax,
                    totalDue,
                    categoryTotals: getCategoryTotals(serviceCharges),
                    paymentMethod: isPaid ? 'Paid From Wallet' : undefined,
                },
                {
                    displayCompany,
                    displaySeller,
                    periodFromDate,
                    periodToDate,
                    hasRentItems,
                }
            );

            console.log('✓ PDF generated and downloaded successfully');
            setIsLoading(false);
        } catch (err) {
            console.error('✗ Error during PDF download:', err);
            const errorMessage = err.message || 'Failed to generate PDF. Please try again.';
            setError(errorMessage);
            setIsLoading(false);
            alert(errorMessage);
        }
    };

    return {
        isLoading,
        error,
        downloadInvoicePDF,
    };
};

/**
 * Helper function to fetch service charges data
 * Replicates the API call from useServiceCharges hook
 * @param {Object} invoice - Invoice record
 * @returns {Promise} { apiData: object|null }
 */
const fetchServiceChargesData = async invoice => {
    try {
        // Import Invoice service dynamically to avoid circular imports
        const { Invoice } = await import('../../../../services/invoice.service');

        const response = await Invoice.getInvoiceLookup(
            invoice.id,
            [invoice.id],
            [invoice.id]
        );

        return {
            apiData: response?.data || null,
        };
    } catch (error) {
        console.error('Error fetching service charges:', error);
        throw new Error('Failed to fetch invoice details. Please try again.');
    }
};

export default usePdfDownload;
